import { useEffect, useState } from 'react';
import { Modal } from '../../../shared/components/Modal';
import Button from '../../../shared/components/Button';
import { Input } from '../../../shared/components/Input';
import type { AddOn, CreateAddOnPayload, PackageTemplate } from '../api/packagesApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  editing: AddOn | null;
  templates: PackageTemplate[];
  onSubmit: (values: CreateAddOnPayload) => Promise<void> | void;
  isSaving: boolean;
}

export function AddOnModal({ isOpen, onClose, editing, templates, onSubmit, isSaving }: Props) {
  const [name, setName] = useState('');
  const [priceNaira, setPriceNaira] = useState('');
  const [extraMins, setExtraMins] = useState('');
  const [packageIds, setPackageIds] = useState<string[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    setName(editing?.name ?? '');
    setPriceNaira(editing ? String((editing.price ?? 0) / 100) : '');
    setExtraMins(editing?.extraDurationMinutes ? String(editing.extraDurationMinutes) : '');
    setPackageIds(editing?.packageIds ?? []);
  }, [isOpen, editing]);

  const toggleTemplate = (id: string) =>
    setPackageIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const priceKobo = Math.round((parseFloat(priceNaira) || 0) * 100);
  const mins = extraMins.trim() === '' ? undefined : Math.max(0, parseInt(extraMins, 10) || 0);
  const canSubmit = name.trim().length > 0 && priceKobo >= 0 && !isSaving;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    await onSubmit({
      name: name.trim(),
      price: priceKobo,
      extraDurationMinutes: mins,
      packageIds,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-foreground">{editing ? 'Edit add-on' : 'New add-on'}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-2xl leading-none" aria-label="Close">
            &times;
          </button>
        </div>

        <div className="space-y-5">
          <Input label="Name" placeholder="e.g. Rush 48h Turnaround" value={name} onChange={(e) => setName(e.target.value)} />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Price (₦)" type="number" min={0} step="0.01" placeholder="0.00" value={priceNaira} onChange={(e) => setPriceNaira(e.target.value)} />
            <Input label="Extra time (mins, optional)" type="number" min={0} placeholder="0" value={extraMins} onChange={(e) => setExtraMins(e.target.value)} />
          </div>

          <div>
            <label className="block text-[14px] font-[600] text-foreground mb-1">Attach to package templates</label>
            {templates.length === 0 ? (
              <p className="text-[13px] text-muted-foreground">No package templates yet — create one first to attach this add-on.</p>
            ) : (
              <div className="max-h-[180px] overflow-auto rounded-[12px] border border-border divide-y divide-border">
                {templates.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-muted">
                    <input
                      type="checkbox"
                      checked={packageIds.includes(t.id)}
                      onChange={() => toggleTemplate(t.id)}
                      className="h-4 w-4 accent-primary"
                    />
                    <span className="text-[14px] text-foreground">{t.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {editing && (
            <p className="text-[12px] text-muted-foreground">Source: {editing.source} (cannot be changed)</p>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-8">
          <Button variant="outline" size="md" type="button" onClick={onClose} disabled={isSaving}>Cancel</Button>
          <Button size="md" type="button" onClick={handleSubmit} loading={isSaving} disabled={!canSubmit}>
            {editing ? 'Save add-on' : 'Create add-on'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
